<template>
  <section class="resources-page">
    <section v-if="!this.$isEngCom">
      <div class="wrapper">
        <div v-for="(section, index) in partnerSections" :key="index">
          <h2 v-if="section.partnerResources.length > 0" class="header uppercase">{{ section.name }}</h2>
          <div class="resource-cards">
            <resource-card v-for="(card, index) in section.partnerResources"
                           :key="index"
                           v-on:click.native="openUrl(card.isVideo, card.url)"
                           :title="card.title"
                           :subtitle="card.description"
                           :image="card.image"
                           :name="section.name + (index + 1)"
                           :isResources="false"
                           :isYscWebinar="false"
                           :hideYscWebinarDate="false"
                           />
          </div>
        </div>
      </div>
      <scroll-to-top />
    </section>
    <section v-if="this.$isEngCom">
      <resource-explore ref="exploreResources" />
    </section>
  </section>
</template>

<script>
import ResourceCard from './ResourceCard'
import ResourceExplore from './ResourceExplore'

import i18n from '@/i18n'
import ScrollToTop from '@/components/Controls/ScrollTopButton'

import componentState from '@/mixins/component-state'
import utmTracking from '@/mixins/utm-tracking'

const exploreStateVars = [
  'selectedCategory',
  'selectedSort',
  'projects',
  'params',
  'offset'
]

export default {
  components: {
    ResourceCard,
    ResourceExplore,
    ScrollToTop
  },
  mixins: [componentState, utmTracking],
  metaInfo: function () {
    return {
      title: this.currentTitle
    }
  },
  data: function () {
    // All subtitles must be under the character limit of 155
    return {
      partnerSections: [],
      partnerResources: [],
      headingTitles: {
        default: i18n.t('furtherProjects'),
        heise: 'Bring deine Projekte voran und hole dir hier weitere Ideen',
        isef: 'Society for Science',
        stsPublicExibition: 'Public Exhibition of Projects (Sunday, March 13 from 2-4pm ET)',
        stsAwards: 'Awards Ceremony (Tuesday, March 15 at 8pm ET)'
      }
    }
  },
  mounted: function () {
    this.getStoredState()
    this.getPartnerResources()
  },
  beforeRouteLeave: function (to, from, next) {
    if (this.$isEngCom) {
      this._set(this.stateObjectKey, this.getState())
    }

    next()
  },
  computed: {
    appName: function () {
      return process.env.APP_NAME
    },
    isHeise: function () {
      return this.appName === 'Heise'
    },
    isIsef: function () {
      return this.appName === 'Isef'
    },
    sections: function () {
      return this.isHeise ? this.heiseSections : this.isIsef ? this.isefSections : this.$isSts ? this.stsSections : this.defaultSection
    },
    defaultSection: function () {
      return [
        {
          heading: this.headingTitles.default,
          cards: this.partnerResources,
          name: `${this.appName.toLowerCase()}-card-`
        }
      ]
    },
    heiseSections: function () {
      return [
        {
          heading: this.headingTitles.heise,
          cards: this.heiseCards,
          name: 'heise-card-'
        },
        {
          heading: this.headingTitles.default,
          cards: this.makeCards,
          name: 'make-card-'
        }
      ]
    },
    isefSections: function () {
      return [
        {
          heading: this.headingTitles.default,
          cards: this.isefCards,
          name: 'isef-card-'
        },
        {
          heading: this.headingTitles.isef,
          cards: this.societyscienceCards,
          name: 'societyscience-card-'
        }
      ]
    },
    stsSections: function () {
      return [
        {
          heading: this.headingTitles.stsPublicExibition,
          cards: this.stsExibitionCards,
          name: 'sts-exibition-card-'
        },
        {
          heading: this.headingTitles.stsAwards,
          cards: this.stsAwardCards,
          name: 'sts-award-card-'
        }
      ]
    },
    currentTitle: function () {
      if (this.$isHeise) {
        return 'Informationen'
      }
      return 'Resources'
    }
  },
  methods: {
    openUrl: function (isVideo, url) {
      if (isVideo) {
        this.$store.dispatch('showVideoTourModal', { fromSourcePage: true, url: url })
      } else {
        window.open(this.getUTMLink(url, 'resources'))
      }
    },
    getState: function () {
      const exploreState = {}
      exploreStateVars.forEach(exploreVar => {
        exploreState[exploreVar] = this.$refs.exploreResources[exploreVar]
      })
      return exploreState
    },
    getStoredState: function () {
      if (!this.$isEngCom) {
        return
      }
      const state = this._pop(this.stateObjectKey)
      this.restoreState(state)
    },
    restoreState: function (state) {
      const hasState = state?.selectedCategory && state?.selectedSort
      if (hasState) {
        exploreStateVars.forEach(exploreVar => {
          this.$refs.exploreResources[exploreVar] = state[exploreVar]
        })
      } else {
        this.$refs.exploreResources.setDefault()
      }
    },
    getPartnerResources: function () {
      this.$store.dispatch('getResourcesByEvent').then((resourcesWithSections) => {
        for (let i = 0; i < resourcesWithSections.length; i++) {
          this.partnerSections.push({
            name: resourcesWithSections[i].name,
            partnerResources: resourcesWithSections[i].resources
          })
        }
      }, (error) => {
        this.errorMessage = error
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  .resources-page {
    z-index: -12;
    background: var(--background-fifth);
    padding-top: 1rem;
    min-height: calc(100vh - #{$navbar-height});
  }

  .uppercase {
    text-transform: uppercase;
  }

  .wrapper {
    width: 80%;
    max-width: 1250px;
    min-width: 1110px;
    padding: 2rem 0 4rem;
    margin: 0 auto;
  }

  .header {
    width: 100%;
    font-family: "Roboto";
    color: $grey-dark;
    font-weight: bold;
    font-size: 1rem;
    padding-bottom: 1rem;
    letter-spacing: 0.05rem;
    border-bottom: 2px solid #ECE9F1;
    position: relative;
    &:after {
      position: absolute;
      content: "";
      height: 2px;
      background-color: $primary;
      width: 90px;
      bottom: -2px;
      left: 0;
    }
  }

    .resource-cards {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      margin-top: 1rem;
      .resource-card {
        margin: 1.5rem 0;
        margin-left: 1rem;
      }
    }

  @media (max-width: 1190px) {
    .wrapper {
      width: 90%;
      min-width: auto;
    }
  }

  @media (max-width: 680px) {
    .resource-cards {
      justify-content: space-around;
    }
  }
</style>
